import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Envelope, Eye, EyeSlash, Key, Lock, Password, SpinnerGap } from '@phosphor-icons/react';
import { internshipAPI } from '../../services/api';

const Field = ({ icon: Icon, className = '', ...props }) => (
  <div className="relative">
    {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />}
    <input {...props} className={`input-field text-base ${Icon ? '!pl-12' : ''} ${className}`} />
  </div>
);

const InternForgot = () => {
  const [mode, setMode] = useState('current');
  const [otpSent, setOtpSent] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({});

  useEffect(() => {
    document.title = 'Forgot password | Growmore IT Services';
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const toggle = (key) => setShow((p) => ({ ...p, [key]: !p[key] }));

  const run = async (payload) => {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const { data } = await internshipAPI.forgot(payload);
      setMessage(data.message || 'Saved.');
      return data;
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update password.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onCurrent = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await run({
      mode: 'current',
      email: String(form.get('email') || '').trim(),
      current_password: String(form.get('current_password') || ''),
      password: String(form.get('password') || ''),
      password_confirmation: String(form.get('password_confirmation') || '')
    });
  };

  const onSendOtp = async (event) => {
    event.preventDefault();
    const data = await run({ mode: 'otp', email: email.trim() });
    if (data) setOtpSent(true);
  };

  const onOtpReset = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await run({
      mode: 'otp_reset',
      email: email.trim(),
      otp: String(form.get('otp') || '').trim(),
      password: String(form.get('password') || ''),
      password_confirmation: String(form.get('password_confirmation') || '')
    });
  };

  const pick = (next) => {
    setMode(next);
    setError('');
    setMessage('');
    if (next === 'current') setOtpSent(false);
  };

  const eye = (key) => (
    <button type="button" onClick={() => toggle(key)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400" aria-label="Show password">
      {show[key] ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  );

  return (
    <div className="min-h-[100dvh] relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800 [&_.input-field]:!bg-white [&_.input-field]:!text-gray-900 [&_.input-field]:!border-gray-200">
      <div className="absolute -top-16 right-10 w-64 h-64 bg-brand-400/15 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 left-8 w-40 h-40 bg-violet-400/10 rounded-full blur-3xl" />

      <div className="relative pt-6 sm:pt-10 pb-10 px-4">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full badge-primary mb-4 text-[11px] font-semibold uppercase tracking-[0.14em]">
              <Key className="w-4 h-4" weight="fill" /> Password help
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight">
              Reset your <span className="text-gradient">password</span>
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
              Use your current password, or get a code from info@growmoreitservices.com on the same email you used at registration.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            <button
              type="button"
              onClick={() => pick('current')}
              className={`rounded-2xl border p-4 text-left smooth-transition ${
                mode === 'current'
                  ? 'bg-white border-brand-300 shadow-soft ring-2 ring-brand-100'
                  : 'bg-white/70 border-gray-100 hover:border-brand-200'
              }`}
            >
              <Lock className="w-5 h-5 text-brand-600 mb-2" weight="duotone" />
              <p className="font-display font-bold text-gray-900">I know my password</p>
              <p className="text-xs text-gray-500 mt-1">Enter the current one and create a new password.</p>
            </button>
            <button
              type="button"
              onClick={() => pick('otp')}
              className={`rounded-2xl border p-4 text-left smooth-transition ${
                mode === 'otp'
                  ? 'bg-white border-brand-300 shadow-soft ring-2 ring-brand-100'
                  : 'bg-white/70 border-gray-100 hover:border-brand-200'
              }`}
            >
              <Envelope className="w-5 h-5 text-brand-600 mb-2" weight="duotone" />
              <p className="font-display font-bold text-gray-900">Email me a code</p>
              <p className="text-xs text-gray-500 mt-1">Code goes to the email you used when you registered.</p>
            </button>
          </div>

          <div className="rounded-3xl bg-white/90 dark:bg-dark-800/90 border border-gray-100 dark:border-gray-800 shadow-soft p-6 sm:p-8 space-y-4">
            {mode === 'current' ? (
              <form onSubmit={onCurrent} className="space-y-3">
                <Field icon={Envelope} name="email" type="email" required autoComplete="username" placeholder="Email" />
                <div className="relative">
                  <Field icon={Lock} name="current_password" type={show.current ? 'text' : 'password'} required autoComplete="current-password" placeholder="Current password" />
                  {eye('current')}
                </div>
                <div className="relative">
                  <Field icon={Password} name="password" type={show.next ? 'text' : 'password'} required minLength={8} autoComplete="new-password" placeholder="New password (min 8)" />
                  {eye('next')}
                </div>
                <div className="relative">
                  <Field icon={Password} name="password_confirmation" type={show.confirm ? 'text' : 'password'} required minLength={8} autoComplete="new-password" placeholder="Confirm new password" />
                  {eye('confirm')}
                </div>
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>}
                {message && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">{message}</p>}
                <button type="submit" disabled={loading} className="btn-primary text-white w-full inline-flex justify-center gap-2">
                  {loading ? <SpinnerGap className="w-5 h-5 animate-spin" /> : 'Save new password'}
                </button>
              </form>
            ) : (
              <div className="space-y-3">
                <form onSubmit={onSendOtp} className="space-y-3">
                  <Field
                    icon={Envelope}
                    name="email"
                    type="text"
                    required
                    autoComplete="username"
                    placeholder="Registration email, intern ID or mobile"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">The code is sent from info@growmoreitservices.com to the email used at internship registration.</p>
                  <button type="submit" disabled={loading} className="w-full px-4 py-3 rounded-xl border border-brand-200 bg-brand-50 text-brand-700 text-sm font-semibold inline-flex justify-center gap-2">
                    {loading && !otpSent ? <SpinnerGap className="w-5 h-5 animate-spin" /> : 'Send code to my registration email'}
                  </button>
                </form>
                {otpSent && (
                  <form onSubmit={onOtpReset} className="space-y-3 pt-2 border-t border-gray-100">
                    <Field name="otp" inputMode="numeric" maxLength={6} required placeholder="6-digit code" className="tracking-[0.4em] text-center font-semibold" />
                    <div className="relative">
                      <Field icon={Password} name="password" type={show.next ? 'text' : 'password'} required minLength={8} autoComplete="new-password" placeholder="New password (min 8)" />
                      {eye('next')}
                    </div>
                    <div className="relative">
                      <Field icon={Password} name="password_confirmation" type={show.confirm ? 'text' : 'password'} required minLength={8} autoComplete="new-password" placeholder="Confirm new password" />
                      {eye('confirm')}
                    </div>
                    <button type="submit" disabled={loading} className="btn-primary text-white w-full inline-flex justify-center gap-2">
                      {loading ? <SpinnerGap className="w-5 h-5 animate-spin" /> : 'Set new password'}
                    </button>
                  </form>
                )}
                {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>}
                {message && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">{message}</p>}
              </div>
            )}
          </div>

          <p className="text-center mt-6">
            <Link to="/intern/login" className="text-sm font-semibold text-brand-600">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default InternForgot;
